import { escapeHtml } from '../lib/html.js';
import { statusPillClass } from '../lib/status.js';
import { G2_ICONS } from '../data/icons.js';
import { SHOPIFY_MARK } from '../data/sections.js';
import { listShopifyLojas, resumoShopify } from '../lib/shopify.js';
import { fmtValor, moedaBase } from '../lib/moeda.js';
import { isAdmin } from '../lib/auth.js';
import { buildHref } from '../lib/router.js';
import { paginate, LIST_PAGE_SIZE } from '../lib/pagination.js';
import { renderPager } from './pager.js';

let lojas = [];
let resumo = { geral: { pedidos: 0, aprovadas: 0, vendido: 0 }, lojas: [], webhooks: {} };
let loaded = false;
let loadedMoeda = '';
let loading = false;
let loadError = '';
let pollTimer = null;
const pendingSync = new Set();

const SYNC_BUSY = new Set(['queued', 'products', 'orders', 'syncing']);

function appShopifyHtml(loja) {
  const instalada = loja.app_version || '1.0.0';
  const atual = loja.app_version_atual || instalada;
  if (!loja.app_desatualizado) {
    return `<div class="store-app store-app-ok"><b>App na Shopify</b><span>Versão ${escapeHtml(instalada)}</span></div>`;
  }
  return `<div class="store-app">
    <div>
      <b>Atualize o app na Shopify</b>
      <p>Esta loja está na versão ${escapeHtml(instalada)}. A versão atual é ${escapeHtml(atual)}.</p>
    </div>
    <button type="button" class="btn btn-primary" data-action="atualizar-app-shopify" data-shop="${escapeHtml(loja.shop_domain)}">Atualizar na Shopify</button>
  </div>`;
}

function isSyncStale(loja) {
  const ts = Date.parse(loja?.updated_at || loja?.last_sync_at || '');
  if (!Number.isFinite(ts)) return true;
  return Date.now() - ts > 3 * 60 * 1000;
}

export function isLojaSyncBusy(loja) {
  if (!loja) return false;
  const running = SYNC_BUSY.has(loja.sync_status) || pendingSync.has(String(loja.id));
  if (!running) return false;
  return !isSyncStale(loja);
}

export function markLojaSyncPending(id) {
  pendingSync.add(String(id));
}

export function clearLojaSyncPending(id) {
  pendingSync.delete(String(id));
}

export function getCachedLoja(id) {
  return lojas.find((item) => String(item.id) === String(id)) || null;
}

export function getShopifyWebhookUrl() {
  return resumo.webhooks?.shopify || '';
}

function stopSyncPoll() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function scheduleSyncPoll(onNeedRefresh) {
  stopSyncPoll();
  const busy = lojas.some((loja) => SYNC_BUSY.has(loja.sync_status));
  if (!busy) return;
  pollTimer = setTimeout(async () => {
    await refreshIntegracoes();
    onNeedRefresh?.();
  }, 3000);
}

export async function refreshIntegracoes() {
  loading = true;
  loadError = '';
  try {
    const [lojasData, resumoData] = await Promise.all([
      listShopifyLojas(),
      resumoShopify({ moeda: moedaBase() }),
    ]);
    lojas = lojasData.lojas || [];
    resumo = resumoData;
    loadedMoeda = moedaBase();
    if (lojasData.webhooks) resumo.webhooks = lojasData.webhooks;
    for (const loja of lojas) {
      if (!SYNC_BUSY.has(loja.sync_status)) pendingSync.delete(String(loja.id));
    }
    loaded = true;
  } catch (err) {
    loadError = err.message || 'Não foi possível carregar as integrações Shopify.';
    lojas = [];
    loaded = true;
    loadedMoeda = moedaBase();
  } finally {
    loading = false;
  }
  return { lojas, resumo };
}

function money(value) {
  return fmtValor(value);
}

function fmtWhen(iso) {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function syncLabel(status) {
  const map = {
    idle: 'parada',
    queued: 'na fila',
    products: 'produtos',
    orders: 'vendas',
    syncing: 'sincronizando',
    done: 'pronta',
    error: 'erro',
  };
  return map[status] || status || '—';
}

function webhookLabel(status) {
  const map = {
    pendente: 'pendente',
    ativo: 'ativo',
    parcial: 'parcial',
    erro: 'erro',
  };
  return map[status] || status || 'pendente';
}

function teaserHtml() {
  const admin = isAdmin();
  const body = admin
    ? `<p><b>Administrador:</b> CLI → escolha a loja → <b>Criar e instalar</b> (app custom na org; ideal para dev stores). Com a flag desligada na API, cai no app publicado da App Store.</p>
        <p class="cell-sub">Se a etapa 4 ficar «aguardando» para sempre, você provavelmente caiu no app publicado sem a loja em Distribution — refaça o wizard após o deploy ou inclua a loja no Partner.</p>`
    : `<p>Informe o domínio <span class="mono">minhaloja.myshopify.com</span> e autorize o app Torre de Controle na Shopify (app remoto / App Store).</p>`;
  return `
    <div class="wizard-teaser">
      <div>
        <h3>Como conectar uma plataforma</h3>
        ${body}
      </div>
      <button type="button" class="btn btn-primary" data-action="loja-wizard">Conectar Shopify</button>
    </div>`;
}

export function renderIntegracoes({ page = 1, onNeedRefresh } = {}) {
  if (loaded && loadedMoeda !== moedaBase()) loaded = false;
  if (!loaded && !loading) {
    refreshIntegracoes().then(() => onNeedRefresh?.());
  }
  if (loaded && !loading) {
    scheduleSyncPoll(onNeedRefresh);
  }
  if (!loaded || loading) {
    return `${teaserHtml()}<div class="empty-state">Carregando plataformas…</div>`;
  }
  if (loadError) {
    return `${teaserHtml()}<div class="empty-state">${escapeHtml(loadError)}</div>`;
  }

  const g = resumo.geral || {};
  const pg = paginate(lojas, page, LIST_PAGE_SIZE);
  const shopCards = pg.total
    ? pg.items.map((loja) => {
        const sync = loja.sync_status || 'idle';
        const hook = loja.webhook_status || 'pendente';
        const totalCount = loja.products_count || 0;
        const ordersCount = Number(loja.orders_count) || 0;
        const busy = isLojaSyncBusy(loja);
        const footHint = loja.sync_error
          ? escapeHtml(loja.sync_error)
          : loja.webhook_error
            ? escapeHtml(loja.webhook_error)
            : loja.last_sync_at
              ? `Sincronizada ${escapeHtml(fmtWhen(loja.last_sync_at))}`
              : 'Ainda não sincronizou';
        return `<article class="g-card store-card">
          <div class="store-card-top">
            <div class="store-card-id">
              <span class="platform-logo shopify" title="Shopify">${SHOPIFY_MARK}</span>
              <div class="store-card-copy">
                <div class="cell-title"><span class="store-card-name">${escapeHtml(loja.nome || loja.shop_domain)}</span><span class="currency-badge">${escapeHtml(loja.currency || 'BRL')}</span></div>
                <div class="cell-sub" title="${escapeHtml(loja.shop_domain)}">${escapeHtml(loja.shop_domain)}</div>
              </div>
            </div>
            <div class="row-actions">
              <button type="button" class="icon-btn icon-only${busy ? ' spinning' : ''}" data-action="sync" data-type="lojasShopify" data-id="${escapeHtml(loja.id)}" ${busy ? 'disabled' : ''} title="${busy ? 'Sincronizando…' : 'Sincronizar'}" aria-label="${busy ? 'Sincronizando' : 'Sincronizar'}">${busy ? G2_ICONS.spin : G2_ICONS.refresh}</button>
              <button type="button" class="icon-btn icon-only" data-action="edit" data-type="lojasShopify" data-id="${escapeHtml(loja.id)}" title="Editar" aria-label="Editar">${G2_ICONS.edit}</button>
              <button type="button" class="icon-btn icon-only danger" data-action="delete" data-type="lojasShopify" data-id="${escapeHtml(loja.id)}" title="Excluir" aria-label="Excluir">${G2_ICONS.trash}</button>
            </div>
          </div>
          <div class="store-card-pills">
            <span class="platform-chip shopify">${SHOPIFY_MARK}<span>Shopify</span></span>
            <span class="pill ${statusPillClass(sync)}">${escapeHtml(syncLabel(sync))}</span>
            <span class="pill ${statusPillClass(hook)}">${escapeHtml(webhookLabel(hook))}</span>
            <span class="pill ${loja.app_desatualizado ? 'pill-amber' : 'pill-green'}">App ${escapeHtml(loja.app_version || '1.0.0')}</span>
          </div>
          ${appShopifyHtml(loja)}
          <div class="store-card-metrics">
            <div class="store-metric">
              <div class="lbl">Produtos</div>
              <div class="val">${totalCount}</div>
            </div>
            <div class="store-metric">
              <div class="lbl">Pedidos</div>
              <div class="val">${ordersCount}</div>
            </div>
          </div>
          <div class="store-card-foot">
            <p class="cell-sub">${footHint}</p>
          </div>
        </article>`;
      }).join('')
    : `<div class="g-card store-card store-card-empty">Nenhuma plataforma. Conecte uma loja Shopify com o domínio.</div>`;

  const produtosTotal = lojas.reduce((sum, loja) => sum + (Number(loja.products_count) || 0), 0);

  return `
    ${teaserHtml()}
    <div class="fat-stats" style="margin-bottom:16px;flex-wrap:wrap">
      <div class="mini-stat">
        <div class="lbl">Lojas</div>
        <div class="val">${lojas.length}</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Produtos</div>
        <div class="val">${produtosTotal}</div>
        <div class="cell-sub">ativos, rascunho e arquivados</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Vendido aprovado (${moedaBase()})</div>
        <div class="val">${escapeHtml(money(g.vendido))}</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Pedidos</div>
        <div class="val">${g.pedidos || 0}</div>
        <div class="cell-sub">${g.aprovadas || 0} pago(s)</div>
      </div>
    </div>
    <div class="table-toolbar">
      <span class="cell-sub">${pg.total} plataforma(s)</span>
    </div>
    <div class="store-grid">${shopCards}</div>
    ${pg.total ? renderPager({
      id: 'lojas',
      page: pg.page,
      totalPages: pg.totalPages,
      from: pg.from,
      to: pg.to,
      total: pg.total,
      label: 'loja(s)',
      hrefFor: (n) => buildHref({ section: 'lojas', page: n }),
    }) : ''}
`;
}
